import {
  ExclamationCircleIcon,
  RefreshIcon,
  ThumbDownIcon,
  ThumbUpIcon,
  ReplyIcon,
} from "@heroicons/react/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WorkflowOutputSuggestion } from "./interface";

interface SuggestionCardProps {
  cardRef?: (node: HTMLDivElement | null) => void;
  suggestion: WorkflowOutputSuggestion;
  index: number;
  isFocused: boolean;
  isActionPending: boolean;
  readOnly?: boolean;
  canRetry: boolean;
  onFocus: () => void;
  onApply: () => void;
  onDismiss: () => void;
  onUndo: () => void;
  onRetry: () => void;
  onSubmitFeedback: (
    feedbackSignal: "POSITIVE" | "NEGATIVE",
    feedbackMessage: string,
  ) => Promise<boolean>;
}

function SuggestionCard(props: SuggestionCardProps) {
  const {
    cardRef,
    suggestion,
    index,
    isFocused,
    isActionPending,
    readOnly,
    canRetry,
    onFocus,
    onApply,
    onDismiss,
    onUndo,
    onRetry,
    onSubmitFeedback,
  } = props;
  const { t } = useTranslation();

  const isPendingSuggestion = suggestion.status === "PENDING";
  const isApplied = suggestion.status === "APPLIED";
  const isSkipped = suggestion.status === "DISMISSED";
  const isError = suggestion.status === "ERROR";
  const isContentEmpty = !suggestion.content?.trim();
  const isMutedSurface = isApplied || isSkipped;
  const canFocus = !readOnly && isPendingSuggestion && !isFocused && !isActionPending;
  const showFocusedActions = !readOnly && isFocused && isPendingSuggestion;
  const showResolvedActions = !readOnly && (isApplied || isSkipped);
  const canUndoResolved = isSkipped || (isApplied && suggestion.hasReverseActions);
  const statusPillClass = isApplied ? "suggestion-card-status-pill-applied" : "suggestion-card-status-pill-skipped";
  const descriptionClass = isSkipped ? "suggestion-card-description-skipped" : "suggestion-card-description";
  const statusLabel = isApplied ? t("Applied") : t("Skipped");
  const suggestionNumber = String(index + 1).padStart(2, "0");
  const isFeedbackInteractionDisabled = !!readOnly || isActionPending;
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackSignalDraft, setFeedbackSignalDraft] = useState<null | "POSITIVE" | "NEGATIVE">(suggestion.feedbackSignal || null);
  const [feedbackMessageDraft, setFeedbackMessageDraft] = useState<string>(suggestion.feedbackMessage || "");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const feedbackFormRef = useRef<HTMLDivElement | null>(null);
  const trimmedFeedbackMessageDraft = feedbackMessageDraft.trim();
  const positiveFeedbackSelected = feedbackSignalDraft === "POSITIVE";
  const negativeFeedbackSelected = feedbackSignalDraft === "NEGATIVE";

  const syncFeedbackDraftFromSuggestion = useCallback(() => {
    setFeedbackSignalDraft(suggestion.feedbackSignal || null);
    setFeedbackMessageDraft(suggestion.feedbackMessage || "");
  }, [suggestion.feedbackSignal, suggestion.feedbackMessage]);

  useEffect(() => {
    syncFeedbackDraftFromSuggestion();
    // If user focuses another pending card, collapse and reset this card's feedback panel.
    if (isPendingSuggestion && !isFocused)
      setShowFeedbackForm(false);
  }, [
    isFocused,
    isPendingSuggestion,
    syncFeedbackDraftFromSuggestion,
  ]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!canFocus) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onFocus();
    }
  }

  function handleCardClickCapture(event: React.MouseEvent<HTMLDivElement>) {
    if (!showFeedbackForm || isFeedbackInteractionDisabled)
      return;
    const target = event.target as Node;
    if (feedbackFormRef.current?.contains(target))
      return;
    handleFeedbackCancel();
  }

  async function handleFeedbackSignalClick(signal: "POSITIVE" | "NEGATIVE") {
    if (isFeedbackInteractionDisabled)
      return;
    const previousSignal = suggestion.feedbackSignal || null;
    setFeedbackSignalDraft(signal);
    setShowFeedbackForm(true);
    setIsSubmittingFeedback(true);
    try {
      const wasSaved = await onSubmitFeedback(signal, "");
      if (!wasSaved)
        setFeedbackSignalDraft(previousSignal);
    } finally {
      setIsSubmittingFeedback(false);
    }
  }

  async function handleFeedbackSubmit() {
    if (!feedbackSignalDraft || isFeedbackInteractionDisabled || !trimmedFeedbackMessageDraft)
      return;
    setIsSubmittingFeedback(true);
    try {
      const wasSaved = await onSubmitFeedback(feedbackSignalDraft, trimmedFeedbackMessageDraft);
      if (wasSaved)
        setShowFeedbackForm(false);
    } finally {
      setIsSubmittingFeedback(false);
    }
  }

  function handleFeedbackCancel() {
    setShowFeedbackForm(false);
    syncFeedbackDraftFromSuggestion();
  }

  function renderFeedbackButtons() {
    return (
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          className={`suggestion-card-feedback-btn ${positiveFeedbackSelected ? "suggestion-card-feedback-btn-active" : ""}`}
          onClick={() => handleFeedbackSignalClick("POSITIVE")}
          disabled={isFeedbackInteractionDisabled}
          aria-label={t("Send positive feedback")}
          aria-pressed={positiveFeedbackSelected}
        >
          <ThumbUpIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          className={`suggestion-card-feedback-btn ${negativeFeedbackSelected ? "suggestion-card-feedback-btn-active" : ""}`}
          onClick={() => handleFeedbackSignalClick("NEGATIVE")}
          disabled={isFeedbackInteractionDisabled}
          aria-label={t("Send negative feedback")}
          aria-pressed={negativeFeedbackSelected}
        >
          <ThumbDownIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={`suggestion-card transition-colors ${
        isMutedSurface ? "suggestion-card-muted" : "suggestion-card-active"
      } ${
        isFocused ? "suggestion-card-focus" : ""
      } ${canFocus ? "cursor-pointer hover:border-gray-300" : ""}`}
      onClickCapture={handleCardClickCapture}
      onClick={canFocus ? onFocus : undefined}
      onKeyDown={handleKeyDown}
      role={canFocus ? "button" : undefined}
      tabIndex={canFocus ? 0 : undefined}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-2">
            <div className="suggestion-card-number">
              {suggestionNumber}
            </div>
            {isError &&
              <div className="inline-flex items-center text-red">
                <ExclamationCircleIcon className="w-3.5 h-3.5" />
              </div>
            }
          </div>
          <div className="inline-flex items-center gap-2 shrink-0">
            {(isApplied || isSkipped) && (
              <div className={statusPillClass}>
                {statusLabel}
              </div>
            )}
          </div>
        </div>
        <div className={descriptionClass}>
          {suggestion.description}
        </div>
      </div>

      {isFocused && isPendingSuggestion && (
        <div className="suggestion-card-content">
          <div className="suggestion-card-content-title">{t("Suggested content")}</div>
          <div className="suggestion-card-content-body">
            {suggestion.content || t("No content provided.")}
          </div>
        </div>
      )}

      {isError && (
        <div className="flex flex-col gap-1 bg-red/10 rounded p-2 text-xs text-red">
          <div className="flex items-start gap-1">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 mt-px" />
            <div className="leading-relaxed">{suggestion.error || "Failed to apply this suggestion."}</div>
          </div>
          <button
            type="button"
            className="self-start flex items-center gap-1 text-red underline font-semibold disabled:opacity-50"
            onClick={onRetry}
            disabled={isActionPending || !canRetry}
          >
            {isActionPending ? (
              <><RefreshIcon className="w-3 h-3 animate-spin" />Retrying…</>
            ) : (
              "Try again"
            )}
          </button>
        </div>
      )}

      {showFocusedActions && (
        <div className="flex items-end justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="suggestion-card-action-apply"
              onClick={onApply}
              disabled={isActionPending}
            >
              {isActionPending && !isSubmittingFeedback ? (
                <>
                  <RefreshIcon className="w-3 h-3 animate-spin" />
                  {isContentEmpty ? t("Marking resolved...") : "Applying…"}
                </>
              ) : (
                isContentEmpty ? t("Mark resolved") : t("Apply")
              )}
            </button>
            <button
              type="button"
              className="suggestion-card-action-skip"
              onClick={onDismiss}
              disabled={isActionPending}
            >
              {t("Skip")}
            </button>
          </div>
          {renderFeedbackButtons()}
        </div>
      )}

      {showResolvedActions && (
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            className="suggestion-card-undo-btn"
            onClick={onUndo}
            disabled={isActionPending || !canUndoResolved}
          >
            <ReplyIcon className="w-3 h-3" />
            {t("Undo")}
          </button>
          {renderFeedbackButtons()}
        </div>
      )}

      {showFeedbackForm && (
        <div ref={feedbackFormRef} className="rounded-lg border border-gray-200 bg-white px-3 py-2 flex flex-col gap-2">
          <label htmlFor={`suggestion-feedback-${suggestion.id}`} className="text-xs font-medium text-gray-500">
            {t("Share optional feedback")}
          </label>
          <textarea
            id={`suggestion-feedback-${suggestion.id}`}
            className="w-full min-h-[80px] rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-700 leading-5 focus:outline-none focus:ring-2 focus:ring-blue resize-y disabled:opacity-50"
            value={feedbackMessageDraft}
            onChange={(event) => setFeedbackMessageDraft(event.target.value)}
            disabled={isFeedbackInteractionDisabled}
            placeholder={t("Tell us more (optional)")}
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="suggestion-card-action-skip h-7 px-3"
              onClick={handleFeedbackCancel}
              disabled={isFeedbackInteractionDisabled}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="suggestion-card-action-apply h-7 px-3"
              onClick={handleFeedbackSubmit}
              disabled={isFeedbackInteractionDisabled || !feedbackSignalDraft || !trimmedFeedbackMessageDraft}
            >
              {isSubmittingFeedback ? (
                <><RefreshIcon className="w-3 h-3 animate-spin" />{t("Submitting…")}</>
              ) : (
                t("Submit")
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuggestionCard;
